import React, { useCallback, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Slider } from '@src/design-system/slider';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';
import { Text } from '@src/design-system/text';
import { SearchZoomIn, SearchZoomOut1 } from 'iconsax-react';

type AvatarCropDialogProps = {
  open: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onConfirm: (file: File) => void;
  title?: string;
  helperText?: string;
  aspect?: number;
  loading?: boolean;
  error?: string | null;
};

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
    image.src = url;
  });

async function getCroppedFile(
  imageSrc: string,
  pixelCrop: Area,
  fileName: string
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const size = Math.max(pixelCrop.width, pixelCrop.height);
  canvas.width = size;
  canvas.height = size;

  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    size,
    size
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error('Failed to create blob from canvas');
      }

      const file = new File([blob], fileName, { type: blob.type });
      resolve(file);
    }, 'image/jpeg');
  });
}

export const AvatarCropDialog: React.FC<AvatarCropDialogProps> = ({
  open,
  imageSrc,
  onClose,
  onConfirm,
  title = 'Edit avatar',
  aspect = 1,
  loading,
  error,
}) => {
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    const fileName = 'avatar.jpeg';
    const file = await getCroppedFile(imageSrc, croppedAreaPixels, fileName);
    onConfirm(file);
  }, [croppedAreaPixels, imageSrc, onConfirm]);

  if (!imageSrc) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <DialogContent align='center'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className='flex flex-col gap-4 pb-0'>
          <div className='relative mx-auto size-[300px] rounded-xl overflow-hidden'>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              mediaProps={{ className: 'object-contain' }}
              objectFit='contain'
              cropShape='round'
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <div className='flex items-center gap-2 text-gray-600'>
            <button
              type='button'
              onClick={() =>
                setZoom((prev) => Math.max(1, Number((prev - 0.1).toFixed(2))))
              }
              className='flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
              aria-label='Zoom out'
            >
              <SearchZoomOut1 />
            </button>
            <Slider
              min={1}
              max={3}
              step={0.1}
              value={[zoom]}
              onValueChange={([value]) => setZoom(value)}
              aria-label='Avatar zoom'
            />
            <button
              type='button'
              onClick={() =>
                setZoom((prev) => Math.min(3, Number((prev + 0.1).toFixed(2))))
              }
              className='flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
              aria-label='Zoom in'
            >
              <SearchZoomIn />
            </button>
          </div>

          {error ? <Text className='text-xs text-red-500'>{error}</Text> : null}
        </DialogBody>
        <DialogFooter>
          <Button
            variant='primaryOutline'
            size='sm'
            type='button'
            onClick={onClose}
            className='w-full'
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            size='sm'
            type='button'
            onClick={handleConfirm}
            isLoading={loading}
            disabled={!croppedAreaPixels}
            className='w-full'
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
